import React, { useRef, useEffect, useState } from 'react';
import axios from 'axios';
import './RouteModal.css';
import { getGradeColor } from './gradeColors';
import { getAssetUrl } from '../utils/assetUrl';

const REACTION_OPTIONS = [
  "🔥 Awesome",
  "💪 Hard!",
  "😃 Fun",
  "🤔 Tricky",
  "😱 Scary",
  "👏 Well set",
  "🧗‍♂️ Classic",
  "😅 Pumped",
  "👍 Like",
];

const RouteModal = ({ route, onClose, routes, setSelectedRoute, onRouteUpdated }) => {
  const [showCompletionForm, setShowCompletionForm] = useState(false);
  const [completionTag, setCompletionTag] = useState('');
  const [gradeVote, setGradeVote] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isFlipped, setIsFlipped] = useState(false);
  const imageRef = useRef(null);
  const wrapperRef = useRef(null);
  const [imgDims, setImgDims] = useState({ left: 0, top: 0, width: 1, height: 1 });
  const [imageAspectRatio, setImageAspectRatio] = useState('1058 / 1158');
  const completions = Array.isArray(route.completions)
    ? route.completions.filter((completion) => completion && typeof completion === 'object')
    : [];
  const routeHolds = Array.isArray(route.holds)
    ? route.holds.filter(
        (hold) =>
          hold &&
          typeof hold === 'object' &&
          Number.isFinite(Number(hold.x)) &&
          Number.isFinite(Number(hold.y))
      )
    : [];
  const routeList = Array.isArray(routes) ? routes : [];

  // Reactions state
  const [reactions, setReactions] = useState([]); // [{reaction: "🔥 Awesome", count: 2}, ...]
  const [sendingReaction, setSendingReaction] = useState(false);
  const routeId = route?._id || '';
  const encodedRouteId = encodeURIComponent(routeId);

  useEffect(() => {
    const updateDims = () => {
      if (!imageRef.current || !wrapperRef.current) return;
      const img = imageRef.current;
      const wrapper = wrapperRef.current;
      // Get natural and displayed sizes
      if (!img.naturalWidth || !img.naturalHeight || !wrapper.offsetWidth || !wrapper.offsetHeight) {
        return;
      }
      setImageAspectRatio(`${img.naturalWidth} / ${img.naturalHeight}`);
      const naturalAR = img.naturalWidth / img.naturalHeight;
      const wrapperAR = wrapper.offsetWidth / wrapper.offsetHeight;
      let width, height, left, top;
      if (naturalAR > wrapperAR) {
        // Image fills width, black bars top/bottom
        width = wrapper.offsetWidth;
        height = width / naturalAR;
        left = 0;
        top = (wrapper.offsetHeight - height) / 2;
      } else {
        // Image fills height, black bars left/right
        height = wrapper.offsetHeight;
        width = height * naturalAR;
        top = 0;
        left = (wrapper.offsetWidth - width) / 2;
      }
      setImgDims({ left, top, width, height });
    };
    updateDims();
    const image = imageRef.current;
    image?.addEventListener('load', updateDims);
    window.addEventListener('resize', updateDims);
    return () => {
      image?.removeEventListener('load', updateDims);
      window.removeEventListener('resize', updateDims);
    };
  }, [route]);

  // Fetch reactions for this route
  useEffect(() => {
    if (!route?._id) return;
    let active = true;
    axios
      .get(`https://bouldering-backend.vercel.app/api/reactions?routeId=${encodedRouteId}`)
      .then(res => {
        if (active) {
          setReactions(
            Array.isArray(res.data)
              ? res.data.filter((reaction) => reaction && typeof reaction === 'object')
              : []
          );
        }
      })
      .catch(() => {
        if (active) setReactions([]);
      });
    return () => {
      active = false;
    };
  }, [encodedRouteId, route?._id]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleReaction = async (reaction) => {
    setSendingReaction(true);
    try {
      await axios.post('https://bouldering-backend.vercel.app/api/reactions', {
        routeId: route._id,
        reaction
      });
      // Refetch reactions after posting
      const res = await axios.get(`https://bouldering-backend.vercel.app/api/reactions?routeId=${encodedRouteId}`);
      setReactions(
        Array.isArray(res.data)
          ? res.data.filter((item) => item && typeof item === 'object')
          : []
      );
    } catch (error) {
      console.error('Error saving reaction:', error);
      setErrorMessage('Failed to save reaction. Please try again.');
    }
    setSendingReaction(false);
  };

  const handleFormToggle = () => {
    setShowCompletionForm(!showCompletionForm);
    setSuccessMessage('');
    setErrorMessage('');
  };

  const handleFlip = () => setIsFlipped(!isFlipped);

  const calculateGradeVoteTally = () => {
    return completions.reduce((tally, completion) => {
      if (completion.votedGrade === 'Too High') return tally - 1;
      if (completion.votedGrade === 'Too Low') return tally + 1;
      return tally;
    }, 0);
  };

  const getUniqueCompletions = (completions) => {
    const seenTags = new Set();
    return completions.filter((completion) => {
      if (seenTags.has(completion.tag)) return false;
      seenTags.add(completion.tag);
      return true;
    });
  };

  const handleSubmit = async () => {
    const normalizedTag = completionTag.trim().toUpperCase();
    if (!/^[A-Z0-9]{3}$/.test(normalizedTag) || !gradeVote) {
      setErrorMessage('Enter a 3-character tag and select a grade vote.');
      setSuccessMessage('');
      return;
    }
    const completionData = {
      tag: normalizedTag,
      votedGrade: gradeVote,
      date: new Date().toISOString(),
    };
    try {
      await axios.post('https://bouldering-backend.vercel.app/api/routes', {
        routeId: route._id,
        completion: completionData,
      });
      setSuccessMessage('Completion added successfully!');
      setErrorMessage('');
      setShowCompletionForm(false);
      setCompletionTag('');
      setGradeVote('');
      onRouteUpdated?.({
        ...route,
        completions: [...completions, completionData],
      });
    } catch (error) {
      console.error('Error adding completion:', error);
      setErrorMessage('Failed to add completion. Please try again.');
      setSuccessMessage('');
    }
  };

  const gradeVoteTally = calculateGradeVoteTally();
  const idx = routeList.findIndex((r) => r._id === route._id);
  const color = getGradeColor(route.difficulty);

  return (
    <div className="route-modal" role="dialog" aria-modal="true" aria-labelledby="route-modal-title">
      <div className={`route-modal-content ${isFlipped ? 'flipped' : ''}`}>
        <div className="route-modal-color-bar" style={{ background: color }} />
        <button className="close" onClick={onClose} aria-label="Close route details" autoFocus>
          &times;
        </button>
        <div className="modal-nav-btns">
          <button
            className="modal-nav-btn"
            onClick={() => idx > 0 && setSelectedRoute(routeList[idx - 1])}
            disabled={idx === 0}
            aria-label="Previous route"
          >
            &#8592;
          </button>
          <button
            className="modal-nav-btn"
            onClick={() => idx < routeList.length - 1 && setSelectedRoute(routeList[idx + 1])}
            disabled={idx === routeList.length - 1}
            aria-label="Next route"
          >
            &#8594;
          </button>
        </div>
        {/* Front View */}
        {!isFlipped && (
          <div className="card-front">
            <h2 id="route-modal-title" style={{ color }}>{route.name || 'Unnamed route'}</h2>
            <p>Difficulty: {route.difficulty}</p>
            <p>Comment: {route.comment}</p>
            <p>Total Completions: {completions.length}</p>
            <p>
              Grade Vote: {route.difficulty}{' '}
              <span className="grade-vote">
                {gradeVoteTally > 0 ? '↑' : gradeVoteTally < 0 ? '↓' : '→'} {gradeVoteTally}
              </span>
            </p>
            <div className="image-wrapper" ref={wrapperRef} style={{ position: 'relative', aspectRatio: imageAspectRatio }}>
              <svg
                width="100%"
                height="100%"
                viewBox={`0 0 ${imgDims.width} ${imgDims.height}`}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 2, pointerEvents: 'none' }}
              >
                <defs>
                  <mask id="hold-mask">
                    <rect x="0" y="0" width={imgDims.width} height={imgDims.height} fill="white" />
                    {routeHolds.map((hold, idx) => {
                      const size = hold.size === 'small' ? 12 : hold.size === 'large' ? 32 : 20;
                      return <circle key={idx} cx={(hold.x / 100) * imgDims.width} cy={(hold.y / 100) * imgDims.height} r={size / 2} fill="black" />;
                    })}
                  </mask>
                </defs>
                <rect x="0" y="0" width={imgDims.width} height={imgDims.height} fill="rgba(0,0,0,0.55)" mask="url(#hold-mask)" />
              </svg>
              {/* The wall image */}
              <img
                ref={imageRef}
                src={getAssetUrl(route.image)}
                alt={route.name || 'Bouldering route'}
                className="selected-image"
                id="route-image"
                style={{
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                }}
              />
              {/* Hold outlines/labels */}
              {routeHolds.map((hold, idx) => {
                  const x = imgDims.left + (hold.x / 100) * imgDims.width;
                  const y = imgDims.top + (hold.y / 100) * imgDims.height;
                  return (
                    <div
                      key={idx}
                      className={`hold ${hold.shape} ${hold.fill} ${hold.size} ${hold.type}`}
                      style={{ position: 'absolute', left: `${x}px`, top: `${y}px`, backgroundColor: hold.fill === 'filled' ? hold.color : 'transparent', borderColor: hold.color, zIndex: 3, transform: 'translate(-50%, -50%)' }}
                    >
                      {hold.type === 'start' && <span className="hold-label">S</span>}
                      {hold.type === 'top' && <span className="hold-label">T</span>}
                    </div>
                  );
                })}
            </div>
            <div className="button-group">
              <button className="completion-button" onClick={handleFormToggle}>
                Mark as Completed
              </button>
              <button
                className="leaderboard-button"
                onClick={handleFlip}
                aria-label="View completions"
                tabIndex={0}
              >
                View Leaderboard
              </button>
            </div>
            {/* --- REACTIONS SECTION --- */}
            <div className="reactions-section">
              <div style={{ marginBottom: 8, fontWeight: 500 }}>Reactions:</div>
              <div className="reactions-list">
                {reactions.length === 0 && <span style={{ color: "#888" }}>No reactions yet</span>}
                {reactions.map((r, i) => (
                  <span key={i} className="reaction-badge">
                    {r.reaction} {r.count > 1 ? `×${r.count}` : ""}
                  </span>
                ))}
              </div>
              <div className="reaction-buttons">
                {REACTION_OPTIONS.map((option) => (
                  <button
                    key={option}
                    className="reaction-btn"
                    disabled={sendingReaction}
                    onClick={() => handleReaction(option)}
                    style={{ margin: 2, fontSize: "1.1em" }}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
            {/* --- END REACTIONS SECTION --- */}
            {showCompletionForm && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSubmit();
                }}
              >
                <label htmlFor="completion-tag">
                  Completion Tag (3 letters):
                  <input
                    id="completion-tag"
                    type="text"
                    value={completionTag}
                    maxLength={3}
                    pattern="[A-Za-z0-9]{3}"
                    required
                    onChange={(e) => setCompletionTag(e.target.value.toUpperCase())}
                  />
                </label>
                <label htmlFor="grade-vote">
                  Grade Vote:
                  <select
                    id="grade-vote"
                    value={gradeVote}
                    required
                    onChange={(e) => setGradeVote(e.target.value)}
                  >
                    <option value="">Select</option>
                    <option value="Correct">Correct</option>
                    <option value="Too High">Too High</option>
                    <option value="Too Low">Too Low</option>
                  </select>
                </label>
                <button type="submit">Submit</button>
              </form>
            )}
            {successMessage && <p className="success-message">{successMessage}</p>}
            {errorMessage && <p className="error-message">{errorMessage}</p>}
          </div>
        )}
        {/* Back View */}
        {isFlipped && (
          <div className="card-back">
            <h3>First Completions</h3>
            <ol>
              {getUniqueCompletions(completions)
                .sort((a, b) => Date.parse(a.date) - Date.parse(b.date))
                .map((completion, index) => (
                  <li key={index}>
                    {completion.tag || 'Unknown tag'} - {Number.isNaN(Date.parse(completion.date))
                      ? 'Unknown date'
                      : new Date(completion.date).toLocaleDateString()}
                  </li>
                ))}
            </ol>
            <button onClick={handleFlip} aria-label="Back to Route">
              Back to Route
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RouteModal;
